import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { RegisterRequestToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { DateInput } from '~/components/date-input';
import { formatDate } from '~/lib/format-date';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useCustomerGenders } from '~/hooks/use-customer-groups';
import { useRegisterRequestMutations } from '~/hooks/use-register-requests';
import { registerRequestSchema, tokenToForm, type RegisterRequestFormValues } from '~/lib/schemas/register-request';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** The row being edited (records are storefront-created — there is no add path). */
	row: RegisterRequestToken | null;
}

/** Edit a register request (Αίτηση Εγγραφής) — read-mostly customer signup record. */
export function RegisterRequestEditDialog({ open, onOpenChange, row }: Props) {
	const { t } = useTranslation();
	const { update } = useRegisterRequestMutations();
	const { data: genders } = useCustomerGenders();

	const form = useForm<RegisterRequestFormValues>({
		resolver: zodResolver(registerRequestSchema) as Resolver<RegisterRequestFormValues>,
		defaultValues: row ? tokenToForm(row) : undefined,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, control, formState } = form;

	useEffect(() => {
		if (open && row) reset(tokenToForm(row));
	}, [open, row, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			await update.mutateAsync({ form: values, original: row });
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const tr = (key: string) => t(`customers.registerRequests.fields.${key}`);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>{t('customers.registerRequests.editTitle')}</DialogTitle>
					<DialogDescription>{t('customers.registerRequests.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<FormField label={tr('email')} error={formState.errors.eml?.message}>
							<Input {...register('eml')} maxLength={255} aria-invalid={Boolean(formState.errors.eml)} autoFocus />
						</FormField>
						<FormField label={tr('createDate')}>
							<Input value={row?.cdt ? formatDate(row.cdt as string) : '—'} disabled readOnly />
						</FormField>
						<FormField label={tr('name')} error={formState.errors.fn?.message}>
							<Input {...register('fn')} maxLength={50} />
						</FormField>
						<FormField label={tr('surname')} error={formState.errors.ln?.message}>
							<Input {...register('ln')} maxLength={50} />
						</FormField>
					</div>

					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<FormField label={tr('company')} error={formState.errors.cnm?.message}>
							<Input {...register('cnm')} maxLength={100} />
						</FormField>
						<FormField label={tr('descriptiveTitle')} error={formState.errors.dtl?.message}>
							<Input {...register('dtl')} maxLength={100} />
						</FormField>
						<FormField label={tr('vat')} error={formState.errors.vat?.message}>
							<Input {...register('vat')} maxLength={20} />
						</FormField>
						<FormField label={tr('irs')} error={formState.errors.irs?.message}>
							<Input {...register('irs')} maxLength={50} />
						</FormField>
						<FormField label={tr('address')} error={formState.errors.adl?.message}>
							<Input {...register('adl')} maxLength={200} />
						</FormField>
					</div>

					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<FormField label={tr('tel')} error={formState.errors.tel?.message}>
							<Input {...register('tel')} maxLength={20} />
						</FormField>
						<FormField label={tr('mobile')} error={formState.errors.mob?.message}>
							<Input {...register('mob')} maxLength={20} />
						</FormField>
						<FormField label={tr('fax')} error={formState.errors.fax?.message}>
							<Input {...register('fax')} maxLength={20} />
						</FormField>
						<FormField label={tr('gender')}>
							<Controller
								control={control}
								name="gd"
								render={({ field }) => (
									<Select
										value={field.value != null ? String(field.value) : 'none'}
										onValueChange={v => field.onChange(v === 'none' ? null : Number(v))}>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="none">—</SelectItem>
											{(genders ?? []).map(g => (
												<SelectItem key={g.id} value={String(g.id)}>
													{g.nm}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>
						</FormField>
						<FormField label={tr('dob')}>
							<Controller
								control={control}
								name="dob"
								render={({ field }) => (
									<DateInput
										value={field.value ? new Date(field.value) : null}
										onChange={d => field.onChange(d ? d.toISOString() : null)}
									/>
								)}
							/>
						</FormField>
					</div>

					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="rr-rnl">{tr('newsletter')}</Label>
							<Switch id="rr-rnl" checked={watch('rnl')} onCheckedChange={next => setValue('rnl', next, { shouldDirty: true })} />
						</div>
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="rr-popt">{tr('promoOptin')}</Label>
							<Switch id="rr-popt" checked={watch('popt')} onCheckedChange={next => setValue('popt', next, { shouldDirty: true })} />
						</div>
					</div>

					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={update.isPending}>
							{update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
